import { useParams, useNavigate } from 'react-router'
import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  useTheme,
  Alert,
  Button,
} from '@mui/material'
import Business from '@mui/icons-material/Business'
import LocationOn from '@mui/icons-material/LocationOn'
import MedicalServices from '@mui/icons-material/MedicalServices'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { styled } from '@mui/material/styles'
import { ClinicAppointmentTypesListing } from 'src/clinic/views/clinicServices/ClinicAppointmentTypes'
import { getCPDataWithParams } from 'src/clinic/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import useServerAuth from 'src/customHooks/useServerAuth'

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: 'calc(100vh - 200px)',
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}))

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.05) 0%, rgba(30, 58, 138, 0.03) 100%)',
  border: '1px solid rgba(17, 39, 71, 0.1)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
}))

const ServicesContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
  },
}))

const LoadingSkeleton = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
}))

const IconBox = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.1) 0%, rgba(30, 58, 138, 0.08) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid rgba(17, 39, 71, 0.15)',
}))

export default function SupportClinicServices() {
  const { clinicSlug } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const { allRolesInBoolean } = useServerAuth()
  const [clinicLocation, setClinicLocation] = useState()
  const [clinicBrand, setClinicBrand] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [managerAssignedBrandIds, setManagerAssignedBrandIds] = useState([])
  const [managerAssignedLocationIds, setManagerAssignedLocationIds] = useState([])

  const isClinicSuccessAdmin = allRolesInBoolean?.isClinicSuccessAdmin
  const isClinicSuccessManager = allRolesInBoolean?.isClinicSuccessManager

  // Clinic Success Manager: only assigned brands/locations. Admin: all.
  const isAuthorizedForClinic = useMemo(() => {
    if (!clinicBrand?.id || !clinicLocation?.id) return true
    if (isClinicSuccessAdmin) return true
    if (isClinicSuccessManager) {
      const brandOk = managerAssignedBrandIds.length === 0 || managerAssignedBrandIds.includes(clinicBrand.id)
      const locationOk = managerAssignedLocationIds.length === 0 || managerAssignedLocationIds.includes(clinicLocation.id)
      return brandOk && locationOk
    }
    return true
  }, [clinicBrand?.id, clinicLocation?.id, isClinicSuccessAdmin, isClinicSuccessManager, managerAssignedBrandIds, managerAssignedLocationIds])

  async function fetchManagerProfile() {
    if (!isClinicSuccessManager) return
    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/clinic-success-manager/profile',
        {},
        undefined,
        undefined,
        true,
      )
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        const assignedBrands = data?.['clinic-brand'] ?? data?.clinicBrands ?? data?.brands ?? []
        const assignedLocations = data?.['clinic'] ?? data?.clinicLocations ?? data?.locations ?? []
        setManagerAssignedBrandIds(assignedBrands.map((b) => b?.id ?? b?.clinicBrandId ?? b?.brandId).filter(Boolean))
        setManagerAssignedLocationIds(assignedLocations.map((l) => l?.id ?? l?.clinicLocationId ?? l?.locationId).filter(Boolean))
      }
    } catch (e) {
      setManagerAssignedBrandIds([])
      setManagerAssignedLocationIds([])
    }
  }

  async function getClinicLocation() {
    const resp = await getCPDataWithParams('/api/secure/cc/support/clinic/by-slug', {
      'clinic-slug': clinicSlug,
      sendBrandDetails: true,
    })
    if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)
      setClinicLocation(data?.['clinic'])
      setClinicBrand(data?.['clinic-brand'])
    } else {
      navigate('/support-home/clinics-hospitals/clinics')
    }
  }

  useEffect(() => {
    if (isClinicSuccessManager) {
      fetchManagerProfile()
    }
  }, [isClinicSuccessManager])

  useEffect(() => {
    if (!clinicSlug || clinicSlug === 'undefined') {
      navigate('/support-home/clinics-hospitals/clinics')
      return
    }

    const stored = sessionStorage.getItem('supportClinicData')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        const matches = parsed?.id === clinicSlug || parsed?.slug === clinicSlug
        if (matches && (parsed?.id || parsed?.clinicBrandId)) {
          setClinicLocation(parsed)
          setClinicBrand({
            id: parsed.clinicBrandId,
            brandName: parsed.clinicBrandName,
            title: parsed.clinicBrandName || parsed.clinicBrandSlug,
          })
        }
      } catch (e) {
        // ignore invalid JSON
      }
    }

    let cancelled = false
    setIsLoading(true)
    getClinicLocation().finally(() => {
      if (!cancelled) setIsLoading(false)
    })
    return () => { cancelled = true }
  }, [clinicSlug, navigate])

  const clinicName = clinicBrand?.brandName || clinicBrand?.title || ''
  const locationName = clinicLocation?.title || ''
  const displayName = clinicName && locationName ? `${clinicName} - ${locationName}` : 'Clinic'

  return (
    <>
      <SupportHeader
        heading={`${displayName} Services`}
        showBackButton
      />
      <Container>
        {isLoading ? (
          <LoadingSkeleton>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mt: 2 }} />
          </LoadingSkeleton>
        ) : (
          <>
            {/* Clinic Information Cards */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              {clinicBrand && (
                <InfoCard sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <IconBox>
                        <Business
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: 24,
                          }}
                        />
                      </IconBox>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        >
                          Clinic Brand
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            color: 'text.primary',
                          }}
                        >
                          {clinicBrand?.brandName || clinicBrand?.title || 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </InfoCard>
              )}

              {clinicLocation && (
                <InfoCard sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <IconBox>
                        <LocationOn
                          sx={{
                            color: theme.palette.secondary.main,
                            fontSize: 24,
                          }}
                        />
                      </IconBox>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        >
                          Location
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            color: 'text.primary',
                          }}
                        >
                          {clinicLocation?.title || 'N/A'}
                        </Typography>
                        {clinicLocation?.address && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              color: 'text.secondary',
                              fontSize: '0.85rem',
                            }}
                          >
                            {clinicLocation.address}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </InfoCard>
              )}
            </Stack>

            {/* Clinic Success Manager: only show services if authorized for this clinic */}
            {!isAuthorizedForClinic ? (
              <ServicesContainer>
                <Box sx={{ p: 3 }}>
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/support-home/clinics-hospitals/clinics')}
                        sx={{ textTransform: 'none' }}
                      >
                        Back to clinics
                      </Button>
                    }
                  >
                    You don&apos;t have access to manage services for this clinic. Clinic Success Managers can only
                    manage appointment types for brands and locations assigned to them. Select a clinic from your
                    assigned list or contact an admin to get access.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    Showing clinic info above for reference. Use &quot;Back to clinics&quot; to choose an assigned clinic.
                  </Typography>
                </Box>
              </ServicesContainer>
            ) : (
              <ServicesContainer>
                <Box
                  sx={{
                    p: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.02) 0%, rgba(30, 58, 138, 0.01) 100%)',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <IconBox>
                      <MedicalServices
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: 24,
                        }}
                      />
                    </IconBox>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        Services & Appointment Types
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Manage and configure clinic services and appointment types
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <ClinicAppointmentTypesListing
                    getDefaultServicesApiSignature="/api/secure/cc/support/clinic/default-templates/list"
                    getServicesApiSignature="/api/secure/cc/support/clinic/clinic-appointment-type/list"
                    getParamsForClinicServices={() => ({
                      'clinic-location-id': clinicLocation?.id,
                      'clinic-brand-id': clinicBrand?.id,
                      page: 1,
                      'page-size': 500,
                    })}
                    clinicId={clinicLocation?.id}
                    clinicBrandId={clinicBrand?.id}
                    clinicLocation={clinicLocation}
                    clinicBrand={clinicBrand}
                    saveAppointmentApiSignature="/api/secure/cc/support/clinic/clinic-appointment-type/save"
                  />
                </Box>
              </ServicesContainer>
            )}
          </>
        )}
      </Container>
    </>
  )
}
